import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { guid } from '@/mixins/guid';
import { modals } from '@/mixins/modal';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';
import path from 'path';

export default Vue.component('edit-image-gallery', {
  template: require('./template.html'),
  mixins: [guid, modals, uploader],
  components: {
    uploadProgress,
  },
  computed: {
    ...mapGetters(['viewport']),
    images() {
      return this.droplet.images;
    },
    minCount() {
      const total = this.droplet.images.length;
      let disableClass = false;
      if (total <= this.imgLimit) {
        disableClass = true;
      }
      return disableClass;
    },
  },
  data() {
    return {
      wait: null,
      paginate: ['images'],
      showImgModal: false,
      activeItem: null,
      loading: false,
      imgLimit: 1,
      prefix: 'drz_image_gallery_',
      bucketPath: `_droplet-assets/imageGallery/${this.droplet._id}/`,
      thumbnails: `_droplet-assets/_thumbnails/imageGallery/${this.droplet._id}/`,
      activeImage: null,
    };
  },
  name: 'edit-image-gallery',
  props: ['droplet', 'show-edit-bubble'],
  methods: {
    getThumbnail(image) {
      let thumb = this._getThumbFromFile(image.src);
      if (image.src.startsWith('/')) {
        thumb = `${this._servePrefix(image.src)}/${this.thumbnails}${thumb}`;
      }
      return `url('${thumb}')`;
    },
    openUpload() {
      this.$refs.uploadFile.click();
      // reset local rearranges on open
      this.$store.dispatch('setImageGalleryLimits', {
        drp: this.droplet,
        showAll: false,
        itemLimit: 4,
      });
    },
    async uploadImages(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          this.loading = true;
          const newFiles = [...e.target.files];
          if (newFiles.length > 0) {
            if (!this.droplet.firstUpload) {
              const payload = {
                drp: this.droplet,
                value: true,
              };
              await this.$store.dispatch('setImageGalleryFirst', payload);
            }
            try {
              // check for expired sessions first
              await this._cognitoCheckSession();
              await Promise.all(newFiles.map(async (f) => {
                let fileName = path.basename(f.name);
                fileName = `${this.prefix}${_.trim(fileName)}`;
                fileName = _.replace(fileName, /\s+/g, '_');
                const filePath = `/${this.bucketPath}${fileName}`;
                await this.uploadFile({
                  file: `${this._sitePath}${filePath}`,
                  body: f,
                  thumbnail: {
                    width: 35,
                    srcFile: f,
                    name: fileName,
                    bucketPath: `${this._sitePath}/${this.thumbnails}`,
                  },
                });
                // update image array
                await this.$store.dispatch('addImageGalleryImage', {
                  drp: this.droplet,
                  newImage: {
                    _id: this.guid(),
                    src: filePath,
                    altText: path.basename(filePath),
                    subTitle: false,
                  },
                });
              }));
            } catch (err) {
              if (!err.message.match(/aborted/gi)) {
                this.$log.error(err);
                await this._reportError({
                  error: err,
                  fn: 'uploadImages - imageGallery',
                });
              }
            } finally {
              this.loading = false;
              this.bouncePlugin();
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          e.target.value = null;
        }
      });
    },
    async showAllImages(e) {
      const checked = e.currentTarget.checked;
      const payload = {};
      payload.drp = this.droplet;
      payload.showAll = checked;
      if (checked) {
        payload.itemLimit = this.droplet.images.length;
      } else {
        payload.itemLimit = 4;
      }
      await this.$store.dispatch('setImageGalleryLimits', payload);
      this.bouncePlugin();
    },
    getImg(image) {
      return path.basename(image.src);
    },
    findIndex(id) {
      // we need to find the index from original array since
      // the template is looping through the paginated array
      let i;
      _.forEach(this.droplet.images, (img, index) => {
        if (img._id === id) {
          i = index;
        }
      });
      return i;
    },
    confirmDelete(image) {
      const total = this.droplet.images.length;
      if (total > this.imgLimit) {
        this.activeItem = image;
        this.toggleModal();
      }
    },
    async removeImg(image) {
      const total = this.droplet.images.length;
      if (total > this.imgLimit) {
        const payload = {};
        payload.drp = this.droplet;
        payload.imgIndex = this.findIndex(image._id);
        // remove image then bounce plugin
        await this.$store.dispatch('removeImageGalleryImg', payload);
        this.bouncePlugin();

        // send off to be deleted from s3 if not default images
        if (this.droplet.firstUpload) {
          this.loading = true;
          // prep notifcation type
          const notif = {
            id: this.guid(),
          };
          const file = path.basename(image.src);
          const thumb = this._getThumbFromFile(image.src);
          try {
            // delete original from s3
            await this._s3deleteFile({
              key: `${this._fullSiteFolder}/${this.bucketPath}${file}`,
            });
            // delete thumbnail from s3
            await this._s3deleteFile({
              key: `${this._fullSiteFolder}/${this.thumbnails}${thumb}`,
            });
            // set for notifications
            notif.type = 'success';
            notif.message = `Successfully deleted image: ${file}`;
          } catch (err) {
            this.$log.error('error: ', err);
            // set for notifications
            notif.type = 'error';
            notif.message = `${err.message || err}. This has been reported.`;
            await this._reportError({
              error: err,
              fn: 'removeImg',
            });
          } finally {
            // send off to notifications
            this.$store.dispatch('addNotification', notif);
            // kill loader
            this.loading = false;
            this.bouncePlugin();
          }
        }
      }
    },
    bouncePlugin() {
      this.$vnode.context.unBindPlugin();
      this.$vnode.context.bindPlugin();
    },
    toggleModal() {
      this.showImgModal = !this.showImgModal;
      const content = this.$refs.confirmModal;
      if (this.showImgModal) {
        this.modelMoveOut(content);
      } else {
        this.modalMoveIn(content);
      }
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
    debounce(fn, delay) {
      clearTimeout(this.wait);
      this.wait = setTimeout(() => {
        fn();
      }, delay);
    },
    setAltText(e, image) {
      this._debounce(() => {
        const text = e.target.value;
        const val = text.trim() !== '' ? text : false;
        this.$store.dispatch('setDropletVal', {
          drp: image,
          key: 'altText',
          val,
        });
      }, 250);
    },
    setSubtitle(e, image) {
      this.debounce(() => {
        const text = e.target.value;
        const val = text.trim() !== '' ? text : false;
        this.$store.dispatch('setDropletVal', {
          drp: image,
          key: 'subTitle',
          val,
        });
        this.bouncePlugin();
      }, 250);
    },
    getImgName(name) {
      return path.basename(name);
    },
    async drzRevertData() {
      this.$emit('css-generator');
      await this._cognitoCheckSession();
      if (this.droplet.firstUpload) {
        const images = this.droplet.images;
        _.forEach(images, async (image, i) => {
          if (!this._isBoilerplateAsset(image.src)) {
            let file = path.basename(image.src);
            file = `${this._sitePath}/${this.bucketPath}${file}`;
            try {
              await this._s3checkFile({ path: file });
            } catch (e) {
              this.$log.warn(e);
              // this is for the event in which a user deletes
              // an image from s3 then tries to cancel. Since we
              // aren't restoring the image in s3, we need to
              // remove it from the images array after the cancel
              // action reverts the other data.
              const payload = {};
              payload.drp = this.droplet;
              payload.imgIndex = i;
              // remove image then bounce plugin
              await this.$store.dispatch('removeImageGalleryImg', payload);
              this.bouncePlugin();
            }
          }
        });
      }
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/image-gallery/components/_edit-options/index.js